import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { FlightSchema } from '../../types/flight';
import { validateFlightData } from '../../utils/flightValidation';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: FlightSchema[]) => Promise<{ message: string }>;
};

export const UploadFlightsModal = ({ isOpen, onClose, onUpload }: Props) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const { translations } = useLanguage();

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const reader = new FileReader();
      
      const readFileAsync = new Promise<string>((resolve, reject) => {
        reader.onload = (e) => {
          if (e.target?.result) {
            resolve(e.target.result as string);
          } else {
            reject('Failed to read file');
          }
        };
        reader.onerror = () => reject('Failed to read file');
        reader.readAsArrayBuffer(file);
      });

      const data = await readFileAsync;
      const workbook = XLSX.read(new Uint8Array(data as unknown as ArrayBuffer), { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Validate and normalize data
      const validatedFlights: FlightSchema[] = [];
      const invalidFlights: { row: number; errors: string[] }[] = [];

      jsonData.forEach((row: any, index) => {
        const validation = validateFlightData(row);
        if (validation.isValid) {
          validatedFlights.push(row as FlightSchema);
        } else {
          invalidFlights.push({ row: index + 2, errors: validation.errors });
        }
      });

      if (invalidFlights.length > 0) {
        const errorMessages = invalidFlights.map(
          f => `Row ${f.row}: ${f.errors.join(', ')}`
        );
        toast.error(`${errorMessages.slice(0, 3).join('\n')}${
          invalidFlights.length > 3 ? `\n...and ${invalidFlights.length - 3} more errors` : ''
        }`);
        setIsUploading(false);
        return;
      }

      const result = await onUpload(validatedFlights);
      toast.success(result.message);
      onClose();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'An error occurred during upload');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.uploadFlightsModal?.title || 'Upload Flights Data'}>
      <div className="space-y-6">
        {/* File Upload Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center ${file ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
        >
          {file ? (
            <div className="text-center">
              <div className="text-xl font-medium text-green-600 mb-1">{file.name}</div>
              <div className="text-gray-500">{(file.size / 1024).toFixed(2)} KB</div>
              <button 
                className="mt-3 text-sm text-red-500 hover:text-red-700"
                onClick={() => setFile(null)}
              >
                {translations?.uploadFlightsModal?.removeFile || 'Remove File'}
              </button>
            </div>
          ) : (
            <div>
              <Upload className="h-12 w-12 mx-auto text-gray-400" />
              <div className="mt-3">
                <label className="cursor-pointer text-primary-600 hover:text-primary-500">
                  <span>{translations?.uploadFlightsModal?.browseFiles || 'Browse Files'}</span>
                  <input type="file" className="sr-only" accept=".xlsx,.xls" onChange={handleFileSelect} />
                </label>
                <span className="text-gray-500"> {translations?.uploadFlightsModal?.orDragAndDrop || 'or drag and drop'}</span>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {translations?.uploadFlightsModal?.excelOnly || 'Excel files only'}
              </div>
            </div>
          )}
        </div>

        {/* Important Notes */}
        <div className="bg-blue-50 text-blue-700 p-4 rounded-lg text-sm">
          <p className="font-medium mb-2">{translations?.uploadFlightsModal?.importantNotes || 'Important Notes'}</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{translations?.uploadFlightsModal?.followFormat || 'Make sure to follow the template format exactly'}</li>
            <li>{translations?.uploadFlightsModal?.dateFormat || 'Dates should be in DD/MM/YYYY format'}</li>
            <li>{translations?.uploadFlightsModal?.timeFormat || 'Times should be in HH:MM:SS AM/PM format'}</li>
            <li>{translations?.uploadFlightsModal?.validAirports || 'Valid airports for Saudi Arabia: Jeddah, Madinah, Taif'}</li>
            <li>{translations?.uploadFlightsModal?.duplicateCheck || 'Duplicate PNR numbers will be rejected'}</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            onClick={onClose}
          >
            {translations?.uploadFlightsModal?.cancel || 'Cancel'}
          </button>
          <button
            className={`px-6 py-2 bg-primary-500 text-white rounded-lg transition-colors ${
              !file || isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600'
            }`}
            disabled={!file || isUploading}
            onClick={handleUpload}
          >
            {isUploading
              ? translations?.uploadFlightsModal?.uploading || 'Uploading...'
              : translations?.uploadFlightsModal?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};